import { useEffect, useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogField,
  DialogFooter,
  dialogInputStyle,
  dialogTextareaStyle,
} from "@/components/ui/Dialog";
import { useAuth } from "@/hooks/useAuth";
import { useCreateBoard } from "@/hooks/useCreateBoard";
import { BOARD_TEMPLATES, BOARD_TEMPLATE_ORDER, type BoardTemplate } from "@/lib/boardTemplates";
import { isFlowAdmin } from "@/lib/memoPermissions";
import { t } from "@/lib/strings";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  navigateOnCreate?: boolean;
  /** Personal-workspace boards are private + owner-less, so ticket boards make no
   *  sense there — hide the Ticketing template and pin to a standard board. */
  isPersonal?: boolean;
}

export function CreateBoardDialog({
  open,
  onOpenChange,
  workspaceId,
  navigateOnCreate = true,
  isPersonal = false,
}: Props) {
  const nameId = useId();
  const descId = useId();
  const tplId = useId();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [template, setTemplate] = useState<BoardTemplate>("standard");
  const [homeListed, setHomeListed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const mutation = useCreateBoard();
  const { user } = useAuth();
  const canUseIdeenbox = isFlowAdmin(user);

  useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
      setTemplate("standard");
      setHomeListed(false);
      setError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const templateOrder = BOARD_TEMPLATE_ORDER.filter((id) => {
    // Personal-workspace boards are private + owner-less, so ticket boards make
    // no sense there.
    if (isPersonal && BOARD_TEMPLATES[id].kind === "tickets") return false;
    // Ideenbox is admin-only during the private rollout.
    if (id === "ideenbox" && !canUseIdeenbox) return false;
    return true;
  });

  // Defensive: if the workspace turns personal while the dialog is open, drop any
  // now-disallowed template selection back to the first allowed one.
  useEffect(() => {
    if (!templateOrder.includes(template)) {
      setTemplate(templateOrder[0] ?? "standard");
    }
  }, [templateOrder, template]);

  const isIdeas = BOARD_TEMPLATES[template].kind === "ideas";
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !mutation.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const board = await mutation.mutateAsync({
        workspaceId,
        payload: {
          name: trimmed,
          description: description.trim() || null,
          home_listed: isIdeas ? homeListed : undefined,
        },
        template,
      });
      onOpenChange(false);
      if (navigateOnCreate) {
        void navigate({ to: "/b/$boardId", params: { boardId: board.id } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t.unknownError);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.createBoardTitle}
      description={t.createBoardDescription}
    >
      <form onSubmit={onSubmit}>
        <DialogField
          label={t.boardTemplateLabel}
          htmlFor={tplId}
          hint={BOARD_TEMPLATES[template].description}
        >
          <select
            id={tplId}
            value={template}
            onChange={(e) => setTemplate(e.target.value as BoardTemplate)}
            style={{ ...dialogInputStyle, height: 36 }}
            data-testid="create-board-template"
          >
            {templateOrder.map((id) => (
              <option key={id} value={id}>
                {BOARD_TEMPLATES[id].label}
              </option>
            ))}
          </select>
        </DialogField>

        {isIdeas ? (
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12 }}>
            <input
              type="checkbox"
              checked={homeListed}
              onChange={(e) => setHomeListed(e.target.checked)}
            />
            <span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{t.ideaHomeVisibleLabel}</span>
              <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}>
                {t.ideaHomeVisibleHint}
              </span>
            </span>
          </label>
        ) : null}

        <DialogField label={t.boardName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.boardNamePlaceholder}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
            data-testid="create-board-name"
          />
        </DialogField>

        <DialogField label={t.boardDescription} htmlFor={descId}>
          <textarea
            id={descId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t.boardDescriptionPlaceholder}
            maxLength={2048}
            style={dialogTextareaStyle}
            data-testid="create-board-description"
          />
        </DialogField>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            data-testid="create-board-submit"
          >
            {mutation.isPending ? t.creating : t.create}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
